/** display_status → theme-aware tokens (light + dark). */

export type StatusColor = {
  /** Soft fill */
  bg: string;
  /** Left rail / dot */
  accent: string;
  /** Primary label */
  text: string;
  /** Secondary line (times) */
  mutedText: string;
  /** Card border */
  border: string;
  label: string;
  shortLabel: string;
};

function status(
  parts: Omit<StatusColor, "label" | "shortLabel"> & { label: string; shortLabel: string }
): StatusColor {
  return parts;
}

export const DISPLAY_STATUS_COLORS: Record<string, StatusColor> = {
  complete: status({
    bg: "bg-emerald-500/15 dark:bg-emerald-500/20",
    accent: "bg-emerald-500 dark:bg-emerald-400",
    text: "text-emerald-900 dark:text-emerald-100",
    mutedText: "text-emerald-800/70 dark:text-emerald-100/60",
    border: "border-emerald-600/15 dark:border-emerald-400/20",
    label: "Complete",
    shortLabel: "Complete",
  }),
  clocked_in: status({
    bg: "bg-sky-500/15 dark:bg-sky-500/20",
    accent: "bg-sky-500 dark:bg-sky-400",
    text: "text-sky-950 dark:text-sky-100",
    mutedText: "text-sky-900/70 dark:text-sky-100/60",
    border: "border-sky-600/15 dark:border-sky-400/20",
    label: "Clocked In",
    shortLabel: "In",
  }),
  late: status({
    bg: "bg-amber-500/15 dark:bg-amber-500/20",
    accent: "bg-amber-500 dark:bg-amber-400",
    text: "text-amber-950 dark:text-amber-100",
    mutedText: "text-amber-900/70 dark:text-amber-100/60",
    border: "border-amber-600/20 dark:border-amber-400/20",
    label: "Late",
    shortLabel: "Late",
  }),
  early_out: status({
    bg: "bg-orange-500/15 dark:bg-orange-500/20",
    accent: "bg-orange-500 dark:bg-orange-400",
    text: "text-orange-950 dark:text-orange-100",
    mutedText: "text-orange-900/70 dark:text-orange-100/60",
    border: "border-orange-600/20 dark:border-orange-400/20",
    label: "Early Out",
    shortLabel: "Early",
  }),
  missing_in: status({
    bg: "bg-red-500/15 dark:bg-red-500/25",
    accent: "bg-red-500 dark:bg-red-400",
    text: "text-red-950 dark:text-red-100",
    mutedText: "text-red-900/70 dark:text-red-100/60",
    border: "border-red-600/20 dark:border-red-400/25",
    label: "Missing In",
    shortLabel: "Miss In",
  }),
  missing_out: status({
    bg: "bg-red-700/12 dark:bg-red-700/30",
    accent: "bg-red-700 dark:bg-red-500",
    text: "text-red-950 dark:text-red-100",
    mutedText: "text-red-900/70 dark:text-red-100/60",
    border: "border-red-800/20 dark:border-red-500/25",
    label: "Missing Out",
    shortLabel: "Miss Out",
  }),
  outside_geofence: status({
    bg: "bg-orange-800/10 dark:bg-orange-800/35",
    accent: "bg-orange-700 dark:bg-orange-500",
    text: "text-orange-950 dark:text-orange-100",
    mutedText: "text-orange-900/70 dark:text-orange-100/60",
    border: "border-orange-800/20 dark:border-orange-500/25",
    label: "Outside Geofence",
    shortLabel: "Geofence",
  }),
  outside_access_hours: status({
    bg: "bg-orange-800/10 dark:bg-orange-800/35",
    accent: "bg-orange-700 dark:bg-orange-500",
    text: "text-orange-950 dark:text-orange-100",
    mutedText: "text-orange-900/70 dark:text-orange-100/60",
    border: "border-orange-800/20 dark:border-orange-500/25",
    label: "Outside Access Hours",
    shortLabel: "Hours",
  }),
  needs_review: status({
    bg: "bg-violet-500/15 dark:bg-violet-500/25",
    accent: "bg-violet-600 dark:bg-violet-400",
    text: "text-violet-950 dark:text-violet-100",
    mutedText: "text-violet-900/70 dark:text-violet-100/60",
    border: "border-violet-600/20 dark:border-violet-400/25",
    label: "Needs Review",
    shortLabel: "Review",
  }),
  time_off: status({
    bg: "bg-slate-500/12 dark:bg-slate-500/25",
    accent: "bg-slate-500 dark:bg-slate-400",
    text: "text-slate-900 dark:text-slate-100",
    mutedText: "text-slate-700/80 dark:text-slate-200/60",
    border: "border-slate-500/20 dark:border-slate-400/20",
    label: "Time Off",
    shortLabel: "Off",
  }),
  time_off_pending: status({
    bg: "bg-amber-500/12 dark:bg-amber-500/15",
    accent: "bg-amber-500 dark:bg-amber-400",
    text: "text-amber-950 dark:text-amber-100",
    mutedText: "text-amber-900/70 dark:text-amber-100/60",
    border: "border-amber-600/20 dark:border-amber-400/20",
    label: "Time Off (Pending)",
    shortLabel: "Off ·",
  }),
  reviewed: status({
    bg: "bg-amber-500/12 dark:bg-amber-500/15",
    accent: "bg-amber-500 dark:bg-amber-400",
    text: "text-amber-950 dark:text-amber-100",
    mutedText: "text-amber-900/70 dark:text-amber-100/60",
    border: "border-amber-600/20 dark:border-amber-400/20",
    label: "Reviewed",
    shortLabel: "Reviewed",
  }),
  excused: status({
    bg: "bg-teal-500/15 dark:bg-teal-500/20",
    accent: "bg-teal-600 dark:bg-teal-400",
    text: "text-teal-950 dark:text-teal-100",
    mutedText: "text-teal-900/70 dark:text-teal-100/60",
    border: "border-teal-600/20 dark:border-teal-400/20",
    label: "Excused",
    shortLabel: "Excused",
  }),
  corrected: status({
    bg: "bg-blue-500/15 dark:bg-blue-500/20",
    accent: "bg-blue-600 dark:bg-blue-400",
    text: "text-blue-950 dark:text-blue-100",
    mutedText: "text-blue-900/70 dark:text-blue-100/60",
    border: "border-blue-600/20 dark:border-blue-400/20",
    label: "Corrected",
    shortLabel: "Fixed",
  }),
};

export function getStatusColor(code: string | null | undefined): StatusColor {
  if (!code) {
    return {
      bg: "bg-transparent",
      accent: "bg-transparent",
      text: "text-muted-foreground",
      mutedText: "text-muted-foreground",
      border: "border-transparent",
      label: "",
      shortLabel: "",
    };
  }
  return (
    DISPLAY_STATUS_COLORS[code] ?? {
      bg: "bg-muted",
      accent: "bg-muted-foreground",
      text: "text-foreground",
      mutedText: "text-muted-foreground",
      border: "border-border",
      label: code,
      shortLabel: code,
    }
  );
}

export const TIME_OFF_APPROVAL = {
  PENDING: 1,
  APPROVED: 2,
  DENIED: 3,
} as const;

export const TIME_OFF_APPROVAL_LABEL: Record<number, string> = {
  1: "Pending",
  2: "Approved",
  3: "Denied",
};

export const NOTE_RELATED_ACTION = {
  NOTE: 1,
  EXCUSE: 2,
  REVIEW: 3,
  CORRECTION: 4,
} as const;

/** Attendance.status workflow integers (integration guide). */
export const ATTENDANCE_WORKFLOW_STATUS = {
  COMPLETE: 1,
  CLOCKED_IN: 2,
  NEEDS_REVIEW: 3,
  REVIEWED: 4,
  EXCUSED: 5,
  CORRECTED: 6,
} as const;

export const SETTLED_WORKFLOW_STATUS_CODES = new Set<number>([
  ATTENDANCE_WORKFLOW_STATUS.REVIEWED,
  ATTENDANCE_WORKFLOW_STATUS.EXCUSED,
  ATTENDANCE_WORKFLOW_STATUS.CORRECTED,
]);
